import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Pagination,
	Select,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import {
	actualizarCarpetaDocumentacion,
	crearCarpetaDocumentacion,
	eliminarCarpetaDocumentacion,
	getCarpetasDocumentacion,
	getListadoDocumentosPersona,
} from "../../services/apis";
import { formatDateTimeEs } from "../../utils/formatters";

const initialFormState = {
	id: null,
	id_campana: "",
	nombre: "",
	descripcion: "",
	roles_permitidos: [],
	requiere_fecha_caducidad: false,
	activa: true,
};

const normalizeText = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const EXPIRY_WARNING_DAYS = 15;

const hasExpiredOrPendingStatus = (estado) =>
	[
		"pendiente",
		"revision",
		"en revision",
		"en_revision",
		"caducado",
		"rechazado",
		"error",
		"inactivo",
	].includes(normalizeText(estado));

const getEstadoColor = (estado) => {
	const normalized = normalizeText(estado);

	if (["vigente", "activo", "ok", "validado", "subido"].includes(normalized)) {
		return "success";
	}

	if (hasExpiredOrPendingStatus(normalized)) {
		return "error";
	}

	return "default";
};

const getExpiryColor = (value) => {
	const normalized = String(value || "").trim().slice(0, 10);

	if (!normalized) {
		return "default";
	}

	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
		return "default";
	}

	const [year, month, day] = normalized.split("-").map(Number);
	const expiryUtc = Date.UTC(year, month - 1, day);
	const today = new Date();
	const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const diffDays = Math.floor((expiryUtc - todayUtc) / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return "error";
	}

	if (diffDays <= EXPIRY_WARNING_DAYS) {
		return "warning";
	}

	return "success";
};

const getDocumentName = (documento) =>
	documento?.nombre || documento?.archivo_nombre || "Documento sin nombre";

const getPersonaName = (documento) =>
	[documento?.persona?.apellidos, documento?.persona?.nombre]
		.filter(Boolean)
		.join(" ")
		.trim() ||
	documento?.persona?.nombre_completo ||
	`Persona ${documento?.id_usuario ?? "-"}`;

const getPersonaNif = (documento) => documento?.persona?.nif || "";

const getCampaignName = (value) => value?.campana_nombre || "Sin campaña";

const normalizeRoleIds = (value) =>
	[...(Array.isArray(value) ? value : [])]
		.map((item) => Number(item))
		.filter((item) => Number.isInteger(item) && item > 0)
		.filter((item, index, array) => array.indexOf(item) === index)
		.sort((left, right) => left - right);

const getFolderRolesSummary = (roleIds, rolesById) => {
	const normalizedIds = normalizeRoleIds(roleIds);

	if (normalizedIds.length === 0) {
		return "Todos los roles";
	}

	const roleNames = normalizedIds
		.map((roleId) => rolesById.get(roleId))
		.filter(Boolean);

	if (roleNames.length === 0) {
		return `${normalizedIds.length} rol(es)`;
	}

	return roleNames.join(", ");
};

export const Carpetas = () => {
	const [carpetas, setCarpetas] = useState([]);
	const [campanas, setCampanas] = useState([]);
	const [rolesDisponibles, setRolesDisponibles] = useState([]);
	const [folderDocuments, setFolderDocuments] = useState([]);
	const [canManage, setCanManage] = useState(false);
	const [canViewDocuments, setCanViewDocuments] = useState(false);
	const [loading, setLoading] = useState(false);
	const [loadingDocuments, setLoadingDocuments] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [documentsError, setDocumentsError] = useState("");
	const [message, setMessage] = useState("");
	const [busqueda, setBusqueda] = useState("");
	const [filtroCampana, setFiltroCampana] = useState("");
	const [filtroEstado, setFiltroEstado] = useState("todas");
	const [page, setPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState(initialFormState);
	const [expandedFolderId, setExpandedFolderId] = useState(null);
	const [defaultCampaignId, setDefaultCampaignId] = useState(0);

	const location = useLocation();
	const isActive = (path) => location.pathname === path;

	const cargarDocumentosCarpetas = useCallback(async () => {
		setLoadingDocuments(true);
		setDocumentsError("");

		try {
			const response = await getListadoDocumentosPersona();

			if (response?.success === true) {
				setFolderDocuments(Array.isArray(response?.data?.documentos) ? response.data.documentos : []);
				return;
			}

			setFolderDocuments([]);
			setDocumentsError(
				response?.message || "No se pudieron cargar los documentos asociados a las carpetas."
			);
		} catch (loadError) {
			console.error("Error cargando documentos por carpeta:", loadError);
			setFolderDocuments([]);
			setDocumentsError("No se pudieron cargar los documentos asociados a las carpetas.");
		} finally {
			setLoadingDocuments(false);
		}
	}, []);

	const cargarCarpetas = useCallback(async () => {
		setLoading(true);
		setError("");
		setDocumentsError("");

		try {
			const response = await getCarpetasDocumentacion();

			if (response?.success !== true) {
				setCarpetas([]);
				setRolesDisponibles([]);
				setCanManage(false);
				setCanViewDocuments(false);
				setError(response?.message || "No se pudieron cargar las carpetas.");
				return;
			}

			setCarpetas(Array.isArray(response?.data?.carpetas) ? response.data.carpetas : []);
			setCampanas(Array.isArray(response?.data?.campanas) ? response.data.campanas : []);
			setRolesDisponibles(Array.isArray(response?.data?.roles) ? response.data.roles : []);
			setDefaultCampaignId(Number(response?.data?.default_campaign_id || 0) || 0);
			setCanManage(response?.data?.can_manage === true);
			const nextCanViewDocuments = response?.data?.can_view_documents === true;
			setCanViewDocuments(nextCanViewDocuments);

			if (nextCanViewDocuments) {
				await cargarDocumentosCarpetas();
			} else {
				setFolderDocuments([]);
			}
		} catch (loadError) {
			console.error("Error cargando carpetas:", loadError);
			setCarpetas([]);
			setRolesDisponibles([]);
			setCanManage(false);
			setCanViewDocuments(false);
			setError("No se pudieron cargar las carpetas.");
		} finally {
			setLoading(false);
		}
	}, [cargarDocumentosCarpetas]);

	useEffect(() => {
		void cargarCarpetas();
	}, [cargarCarpetas]);

	useEffect(() => {
		setPage(1);
	}, [busqueda, filtroCampana, filtroEstado, itemsPerPage]);

	const campanasDisponibles = useMemo(
		() =>
			[...campanas].sort((left, right) =>
				String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
			),
		[campanas]
	);

	const rolesById = useMemo(
		() =>
			new Map(
				rolesDisponibles.map((rol) => [
					Number(rol?.id || 0),
					String(rol?.nombre || "").trim(),
				])
			),
		[rolesDisponibles]
	);

	const carpetasFiltradas = carpetas.filter((carpeta) => {
		const search = normalizeText(busqueda);
		const matchesSearch =
			search === "" ||
			[
				carpeta?.nombre,
				carpeta?.descripcion,
				carpeta?.slug,
				carpeta?.campana_nombre,
				carpeta?.campana_slug,
			].some((value) =>
				normalizeText(value).includes(search)
			);
		const matchesCampaign =
			filtroCampana === "" ||
			Number(carpeta?.id_campana || 0) === Number(filtroCampana);

		const matchesEstado =
			filtroEstado === "todas" ||
			(filtroEstado === "activas" && carpeta?.activa === true) ||
			(filtroEstado === "inactivas" && carpeta?.activa !== true);

		return matchesSearch && matchesCampaign && matchesEstado;
	});

	const totalPages = Math.max(1, Math.ceil(carpetasFiltradas.length / itemsPerPage));
	const startIndex = (page - 1) * itemsPerPage;
	const carpetasPaginadas = carpetasFiltradas.slice(startIndex, startIndex + itemsPerPage);

	const getDocumentsForFolder = (folderId) =>
		folderDocuments.filter(
			(documento) => Number(documento?.id_carpeta_documentacion || 0) === Number(folderId || 0)
		);

	const toggleFolder = (folderId) => {
		setExpandedFolderId((current) => (current === folderId ? null : folderId));
	};

	const abrirNuevaCarpeta = () => {
		setForm({
			...initialFormState,
			id_campana: defaultCampaignId || Number(campanasDisponibles[0]?.id || 0) || "",
			roles_permitidos: [],
		});
		setDialogOpen(true);
		setMessage("");
		setError("");
	};

	const abrirEdicion = (carpeta) => {
		setForm({
			id: carpeta?.id || null,
			id_campana: Number(carpeta?.id_campana || 0) || "",
			nombre: carpeta?.nombre || "",
			descripcion: carpeta?.descripcion || "",
			roles_permitidos: normalizeRoleIds(carpeta?.roles_permitidos),
			requiere_fecha_caducidad: carpeta?.requiere_fecha_caducidad === true,
			activa: carpeta?.activa !== false,
		});
		setDialogOpen(true);
		setMessage("");
		setError("");
	};

	const cerrarDialogo = () => {
		if (saving) return;
		setDialogOpen(false);
		setForm(initialFormState);
		setError("");
	};

	const guardarCarpeta = async () => {
		if (!Number(form.id_campana || 0)) {
			setError("Debes seleccionar una campaña para la carpeta.");
			return;
		}

		if (!form.nombre.trim()) {
			setError("El nombre de la carpeta es obligatorio.");
			return;
		}

		setSaving(true);
		setError("");
		setMessage("");

		try {
			const payload = {
				id: form.id,
				id_campana: Number(form.id_campana || 0),
				nombre: form.nombre.trim(),
				descripcion: form.descripcion.trim(),
				roles_permitidos: normalizeRoleIds(form.roles_permitidos),
				requiere_fecha_caducidad: form.requiere_fecha_caducidad,
				activa: form.activa,
			};

			const response = form.id
				? await actualizarCarpetaDocumentacion(payload)
				: await crearCarpetaDocumentacion(payload);

			if (response?.success !== true) {
				setError(response?.message || "No se pudo guardar la carpeta.");
				return;
			}

			setDialogOpen(false);
			setForm(initialFormState);
			setMessage(
				response?.message ||
				(form.id ? "Carpeta actualizada correctamente." : "Carpeta creada correctamente.")
			);
			await cargarCarpetas();
		} catch (saveError) {
			console.error("Error guardando carpeta:", saveError);
			setError(saveError?.response?.data?.message || "No se pudo guardar la carpeta.");
		} finally {
			setSaving(false);
		}
	};

	const borrarCarpeta = async (carpeta) => {
		if (!carpeta?.id) return;

		const confirmed = window.confirm(
			`Se eliminara la carpeta "${carpeta.nombre}". Esta accion solo se permite si no tiene documentos asociados.`
		);

		if (!confirmed) {
			return;
		}

		setError("");
		setMessage("");

		try {
			const response = await eliminarCarpetaDocumentacion(carpeta.id);

			if (response?.success !== true) {
				setError(response?.message || "No se pudo eliminar la carpeta.");
				return;
			}

			setMessage(response?.message || "Carpeta eliminada correctamente.");
			await cargarCarpetas();
		} catch (deleteError) {
			console.error("Error eliminando carpeta:", deleteError);
			setError(
				deleteError?.response?.data?.message || "No se pudo eliminar la carpeta."
			);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Documentacion" />
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }} alignItems="center">


				<Button
					variant="text"
					component={RouterLink}
					to="/carpetas"
					sx={{
						fontWeight: isActive("/carpetas") ? 600 : 400,
						borderBottom: isActive("/carpetas") ? "2px solid" : "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Carpetas
				</Button>

				<Button
					variant="text"
					component={RouterLink}
					to="/documentacion"
					sx={{
						fontWeight: isActive("/documentacion") ? 600 : 400,
						borderBottom: isActive("/documentacion")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Documentos
				</Button>
				<Button
					variant="text"
					component={RouterLink}
					to="/personas"
					sx={{
						fontWeight: isActive("/personas") ? 600 : 400,
						borderBottom: isActive("/personas") ? "2px solid" : "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Personas
				</Button>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 2, pb: 2 }}
				gap={2}
			>
				<Grid container spacing={1} size={{ xs: 12, md: "auto" }}>
					<TextField
						size="small"
						placeholder="Buscar carpeta o campaña..."
						value={busqueda}
						onChange={(event) => setBusqueda(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{ width: { xs: "100%", md: 320 } }}
					/>

					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel>Campaña</InputLabel>
						<Select
							value={filtroCampana}
							label="Campaña"
							onChange={(event) => setFiltroCampana(event.target.value)}
						>
							<MenuItem value="">Todas</MenuItem>
							{campanasDisponibles.map((campana) => (
								<MenuItem key={`folder-campaign-filter-${campana.id}`} value={String(campana.id)}>
									{campana.nombre}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Estado</InputLabel>
						<Select
							value={filtroEstado}
							label="Estado"
							onChange={(event) => setFiltroEstado(event.target.value)}
						>
							<MenuItem value="todas">Todas</MenuItem>
							<MenuItem value="activas">Activas</MenuItem>
							<MenuItem value="inactivas">Inactivas</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				<Grid container spacing={1} size={{ xs: 12, md: "auto" }}>
					<Button
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={cargarCarpetas}
						disabled={loading}
					>
						Actualizar
					</Button>

					{canManage && (
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={abrirNuevaCarpeta}
						>
							Nueva carpeta
						</Button>
					)}
				</Grid>
			</Grid>

			<Grid container sx={{ px: 2, pb: 2 }}>
				<Alert severity="info" sx={{ width: "100%" }}>
					Las carpetas ahora dependen de una campaña. Desde aqui puedes gestionar la
					estructura documental de cada campaña y revisar sus documentos asociados si
					tu rol tambien tiene acceso al listado documental.
				</Alert>
			</Grid>

			{message && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert severity="success" sx={{ width: "100%" }}>
						{message}
					</Alert>
				</Grid>
			)}

			{error && !dialogOpen && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert severity="error" sx={{ width: "100%" }}>
						{error}
					</Alert>
				</Grid>
			)}

			{!canManage && !loading && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert severity="warning" sx={{ width: "100%" }}>
						Tienes acceso de consulta a las carpetas, pero no permisos para
						gestionarlas.
					</Alert>
				</Grid>
			)}

			{!loading && !canViewDocuments && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert severity="info" sx={{ width: "100%" }}>
						Tu rol puede ver carpetas, pero no el detalle de documentos asociados.
					</Alert>
				</Grid>
			)}

			{!loading && documentsError && canViewDocuments && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert severity="warning" sx={{ width: "100%" }}>
						{documentsError}
					</Alert>
				</Grid>
			)}

			{loading ? (
				<Grid container justifyContent="center" sx={{ py: 5 }}>
					<CircularProgress />
				</Grid>
			) : (
				<>
					<Grid sx={{ width: "100%", overflowX: "auto" }}>
						<Grid container direction="column" sx={{ minWidth: { xs: 1240, md: 1460 } }}>
							<Grid
								container
								alignItems="center"
								sx={{
									px: 2,
									py: 1,
									bgcolor: "grey.100",
									borderTop: "1px solid",
									borderBottom: "1px solid",
									borderColor: "divider",
								}}
							>
								<Grid item size={{ xs: 0.6 }} />
								<Grid item size={{ xs: 2.2 }}>Nombre</Grid>
								<Grid item size={{ xs: 2.0 }}>Campaña</Grid>
								<Grid item size={{ xs: 2.2 }}>Descripcion</Grid>
								<Grid item size={{ xs: 1.3 }}>Caducidad</Grid>
								<Grid item size={{ xs: 1.1 }}>Estado</Grid>
								<Grid item size={{ xs: 1.0 }}>Docs</Grid>
								<Grid item size={{ xs: 1.4 }}>Actualizacion</Grid>
								<Grid item size={{ xs: 1.3 }}>Acciones</Grid>
							</Grid>

							{carpetasPaginadas.length === 0 ? (
								<Grid container justifyContent="center" sx={{ py: 4 }}>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											gap: 1.5,
										}}
									>
										<Typography color="text.secondary">
											No hay carpetas que coincidan con la busqueda.
										</Typography>
										{canManage && (
											<Button
												variant="contained"
												startIcon={<AddIcon />}
												onClick={abrirNuevaCarpeta}
											>
												Crear carpeta
											</Button>
										)}
									</Box>
								</Grid>
							) : (
								carpetasPaginadas.map((carpeta) => {
									const isExpanded = expandedFolderId === carpeta.id;
									const documentosCarpeta = getDocumentsForFolder(carpeta.id);

									return (
										<React.Fragment key={carpeta.id}>
											<Grid
												container
												alignItems="center"
												sx={{
													px: 2,
													py: 1.25,
													borderBottom: isExpanded ? "none" : "1px solid",
													borderColor: "divider",
													"&:hover": { bgcolor: "action.hover" },
												}}
											>
												<Grid item size={{ xs: 0.6 }}>
													{canViewDocuments ? (
														<IconButton size="small" onClick={() => toggleFolder(carpeta.id)}>
															{isExpanded ? (
																<ExpandMoreIcon fontSize="small" />
															) : (
																<ChevronRightIcon fontSize="small" />
															)}
														</IconButton>
													) : null}
												</Grid>

												<Grid
													item
													size={{ xs: 2.2 }}
													sx={{ display: "flex", alignItems: "center", gap: 1 }}
												>
													<FolderOpenOutlinedIcon color="action" fontSize="small" />
													<Box sx={{ minWidth: 0 }}>
														<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
															{carpeta.nombre}
														</Typography>
														<Typography variant="caption" color="text.secondary" noWrap>
															{carpeta.slug}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
															noWrap
															sx={{ display: "block" }}
														>
															Acceso: {getFolderRolesSummary(carpeta?.roles_permitidos, rolesById)}
														</Typography>
													</Box>
												</Grid>

												<Grid item size={{ xs: 2.0 }}>
													<Box sx={{ minWidth: 0 }}>
														<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
															{getCampaignName(carpeta)}
														</Typography>
														<Typography variant="caption" color="text.secondary" noWrap>
															{carpeta?.campana_slug || "-"}
														</Typography>
													</Box>
												</Grid>

												<Grid item size={{ xs: 2.2 }}>
													<Typography variant="body2" color="text.secondary" noWrap>
														{carpeta.descripcion || "-"}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 1.3 }}>
													<Typography variant="body2">
														{carpeta.requiere_fecha_caducidad
															? "Obligatoria"
															: "Opcional"}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 1.1 }}>
													<Typography
														variant="body2"
														color={carpeta.activa ? "success.main" : "text.secondary"}
													>
														{carpeta.activa ? "Activa" : "Inactiva"}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 1.0 }}>
													<Typography variant="body2">{carpeta.total_documentos || 0}</Typography>
												</Grid>

												<Grid item size={{ xs: 1.4 }}>
													<Typography variant="body2" noWrap>
														{formatDateTimeEs(carpeta.fecha_actualizacion, {
															includeTime: false,
														})}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 1.3 }}>
													<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
														{canManage && (
															<Button
																size="small"
																variant="text"
																onClick={() => abrirEdicion(carpeta)}
																sx={{ minWidth: 0, px: 1 }}
															>
																<EditOutlinedIcon fontSize="small" />
															</Button>
														)}
														{canManage && (
															<Button
																size="small"
																variant="text"
																color="error"
																onClick={() => borrarCarpeta(carpeta)}
																sx={{ minWidth: 0, px: 1 }}
															>
																<DeleteOutlineIcon fontSize="small" />
															</Button>
														)}
													</Box>
												</Grid>
											</Grid>

											{canViewDocuments && (
												<Grid item size={{ xs: 12 }}>
													<Collapse in={isExpanded}>
														<Box
															sx={{
																px: 3,
																py: 2,
																bgcolor: "grey.50",
																borderBottom: "1px solid",
																borderColor: "divider",
															}}
														>
															<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
																Documentos asociados a {carpeta.nombre}
															</Typography>

															{loadingDocuments ? (
																<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																	<CircularProgress size={18} />
																	<Typography variant="body2">
																		Cargando documentos...
																	</Typography>
																</Box>
															) : documentsError ? (
																<Alert severity="warning">{documentsError}</Alert>
															) : documentosCarpeta.length === 0 ? (
																<Alert severity="info">
																	Esta carpeta todavia no tiene documentos subidos.
																</Alert>
															) : (
																<Grid container direction="column">
																	<Grid
																		container
																		alignItems="center"
																		sx={{
																			px: 1,
																			py: 1,
																			bgcolor: "common.white",
																			borderRadius: 1,
																			border: "1px solid",
																			borderColor: "divider",
																			fontWeight: 600,
																		}}
																	>
																		<Grid item size={{ xs: 3.2 }}>Documento</Grid>
																		<Grid item size={{ xs: 2.8 }}>Persona</Grid>
																		<Grid item size={{ xs: 1.3 }}>Estado</Grid>
																		<Grid item size={{ xs: 1.4 }}>Caducidad</Grid>
																		<Grid item size={{ xs: 1.3 }}>Formato</Grid>
																		<Grid item size={{ xs: 2.0 }}>Subido</Grid>
																	</Grid>

																	{documentosCarpeta.map((documento) => (
																		<Grid
																			key={`folder-doc-${carpeta.id}-${documento.id}`}
																			container
																			alignItems="center"
																			sx={{
																				px: 1,
																				py: 1,
																				borderLeft: "1px solid",
																				borderRight: "1px solid",
																				borderBottom: "1px solid",
																				borderColor: "divider",
																				bgcolor: "common.white",
																			}}
																		>
																			<Grid
																				item
																				size={{ xs: 3.2 }}
																				sx={{ display: "flex", alignItems: "center", gap: 1 }}
																			>
																				<InsertDriveFileIcon fontSize="small" color="action" />
																				<Box sx={{ minWidth: 0 }}>
																					<Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
																						{getDocumentName(documento)}
																					</Typography>
																					<Typography variant="caption" color="text.secondary" noWrap>
																						{documento?.archivo_nombre || "-"}
																					</Typography>
																				</Box>
																			</Grid>

																			<Grid item size={{ xs: 2.8 }}>
																				<Box
																					component={documento?.id_usuario ? RouterLink : "div"}
																					to={documento?.id_usuario ? `/personas/${documento.id_usuario}` : undefined}
																					state={
																						documento?.id_usuario
																							? {
																								persona: {
																									id: documento.id_usuario,
																									nombre: documento?.persona?.nombre || "",
																									apellidos: documento?.persona?.apellidos || "",
																									nif: documento?.persona?.nif || "",
																									nombre_completo: documento?.persona?.nombre_completo || "",
																								},
																								from: "/carpetas",
																							}
																							: undefined
																					}
																					sx={{
																						cursor: documento?.id_usuario ? "pointer" : "default",
																						textDecoration: "none",
																						color: "inherit",
																						"&:hover .persona-link": documento?.id_usuario
																							? {
																								color: "primary.main",
																								textDecoration: "underline",
																							}
																							: undefined,
																					}}
																				>
																					<Typography
																						className="persona-link"
																						variant="body2"
																						noWrap
																						sx={{ fontWeight: 600, transition: "color 0.2s ease" }}
																					>
																						{getPersonaName(documento)}
																					</Typography>
																					{getPersonaNif(documento) && (
																						<Typography
																							className="persona-link"
																							variant="caption"
																							color="text.secondary"
																							sx={{ transition: "color 0.2s ease" }}
																							noWrap
																						>
																							{getPersonaNif(documento)}
																						</Typography>
																					)}
																				</Box>
																			</Grid>

																			<Grid item size={{ xs: 1.3 }}>
																				<Chip
																					size="small"
																					color={getEstadoColor(documento?.estado)}
																					label={documento?.estado || "-"}
																				/>
																			</Grid>

																			<Grid item size={{ xs: 1.4 }}>
																				{documento?.fecha_caducidad ? (
																					<Chip
																						size="small"
																						color={getExpiryColor(documento.fecha_caducidad)}
																						label={formatDateTimeEs(documento.fecha_caducidad, {
																							includeTime: false,
																						})}
																					/>
																				) : (
																					<Typography variant="body2" color="text.secondary">
																						-
																					</Typography>
																				)}
																			</Grid>

																			<Grid item size={{ xs: 1.3 }}>
																				<Chip
																					size="small"
																					variant="outlined"
																					label={String(documento?.extension || "-").toUpperCase()}
																				/>
																			</Grid>

																			<Grid item size={{ xs: 2.0 }}>
																				<Typography variant="body2" noWrap>
																					{formatDateTimeEs(documento?.fecha_creacion)}
																				</Typography>
																			</Grid>
																		</Grid>
																	))}
																</Grid>
															)}
														</Box>
													</Collapse>
												</Grid>
											)}
										</React.Fragment>
									);
								})
							)}
						</Grid>
					</Grid>

					{carpetasFiltradas.length > 0 && (
						<Grid
							container
							justifyContent="space-between"
							alignItems="center"
							sx={{ px: 2, py: 2 }}
							gap={2}
						>
							<FormControl variant="standard" sx={{ minWidth: 140 }}>
								<InputLabel>Filas por pagina</InputLabel>
								<Select
									value={itemsPerPage}
									onChange={(event) => setItemsPerPage(Number(event.target.value))}
								>
									<MenuItem value={10}>10</MenuItem>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
								</Select>
							</FormControl>

							<Typography variant="body2" color="text.secondary">
								{carpetasFiltradas.length} carpeta(s) encontradas
							</Typography>

							<Pagination
								count={totalPages}
								page={page}
								onChange={(_, value) => setPage(value)}
								color="primary"
							/>
						</Grid>
					)}
				</>
			)}

			<Dialog open={dialogOpen} onClose={cerrarDialogo} fullWidth maxWidth="sm">
				<DialogTitle>{form.id ? "Editar carpeta" : "Nueva carpeta"}</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
						<Typography variant="body2" color="text.secondary">
							Configura aqui una carpeta documental y asignala a la campaña en la
							que debe aparecer.
						</Typography>

						{error && <Alert severity="error">{error}</Alert>}

						<FormControl fullWidth required>
							<InputLabel>Campaña</InputLabel>
							<Select
								value={form.id_campana}
								label="Campaña"
								onChange={(event) =>
									setForm((current) => ({
										...current,
										id_campana: event.target.value,
									}))
								}
							>
								{campanasDisponibles.map((campana) => (
									<MenuItem key={`folder-form-campaign-${campana.id}`} value={campana.id}>
										{campana.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<TextField
							label="Nombre"
							value={form.nombre}
							onChange={(event) =>
								setForm((current) => ({ ...current, nombre: event.target.value }))
							}
							fullWidth
							required
						/>

						<TextField
							label="Descripcion"
							value={form.descripcion}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									descripcion: event.target.value,
								}))
							}
							fullWidth
							multiline
							minRows={2}
						/>

						<FormControl fullWidth>
							<InputLabel>Roles</InputLabel>
							<Select
								multiple
								displayEmpty
								value={form.roles_permitidos}
								label="Roles"
								onChange={(event) =>
									setForm((current) => ({
										...current,
										roles_permitidos: normalizeRoleIds(event.target.value),
									}))
								}
								renderValue={(selected) => getFolderRolesSummary(selected, rolesById)}
							>
								{rolesDisponibles.length === 0 && (
									<MenuItem disabled value="">
										No hay roles disponibles
									</MenuItem>
								)}
								{rolesDisponibles.map((rol) => {
									const roleId = Number(rol?.id || 0);

									return (
										<MenuItem key={`folder-role-${roleId}`} value={roleId}>
											<Checkbox checked={form.roles_permitidos.includes(roleId)} />
											<ListItemText primary={rol?.nombre || `Rol ${roleId}`} />
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<Typography variant="caption" color="text.secondary">
							Si no seleccionas ningun rol, la carpeta estara disponible para todos
							los roles con acceso al modulo de documentacion.
						</Typography>

						<FormControlLabel
							control={
								<Switch
									checked={form.requiere_fecha_caducidad}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											requiere_fecha_caducidad: event.target.checked,
										}))
									}
								/>
							}
							label="La fecha de expiracion sera obligatoria"
						/>

						<FormControlLabel
							control={
								<Switch
									checked={form.activa}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											activa: event.target.checked,
										}))
									}
								/>
							}
							label="Carpeta activa para nuevas subidas"
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={cerrarDialogo} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={guardarCarpeta} disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
